<template>
  <div
    class="cart-control"
    :class="{
      'cart-control--lg': props.size === 'lg',
      'cart-control--active': props.variant === 'active',
      'cart-control--disabled': props.disabled,
    }"
  >
    <button
      type="button"
      class="cart-control__decrease"
      :aria-describedby="'cart-control-' + id + '-label'"
      :disabled="props.disabled || modelValue <= min"
      @click="decrease(); $event.preventDefault()"
    >
      {{ $t("common.decrease") }}
    </button>

    <label
      :id="'cart-control-' + id + '-label'"
      :for="'cart-control-' + id"
      class="cart-control__label visually-hidden"
    >
      {{ $t("cart.quantityLabel", { title: props.title?.toLocaleLowerCase() ?? "" }) }}
    </label>

    <!-- Prefix, input and suffix are centered as one group -->
    <div class="cart-control__value">
      <span v-if="props.prefix" class="cart-control__prefix">
        {{ props.prefix }}
      </span>

      <input
        :id="'cart-control-' + id"
        v-model="model"
        type="text"
        class="cart-control__input"
        inputmode="numeric"
        maxlength="3"
        :name="props.name"
        :aria-describedby="'cart-control-' + id + '-label'"
        :disabled="props.disabled"
        :style="{
          '--cart-value-length': Math.max(
            String(model ?? '').length,
            1,
          ),
        }"
        @blur="onBlur($event)"
      />

      <span v-if="props.suffix" class="cart-control__suffix">
        {{ props.suffix }}
      </span>
    </div>

    <button
      ref="increaseRef"
      type="button"
      class="cart-control__increase"
      :disabled="props.disabled || modelValue >= max"
      :aria-describedby="'cart-control-' + id + '-label'"
      @click="increase(); $event.preventDefault()"
    >
      {{ $t("common.increase") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

const props = defineProps<{
  title: string;
  modelValue: number;
  name?: string;
  disabled?: boolean;
  size?: "lg";
  variant?: "active";
  prefix?: string;
  suffix?: string;
  step?: number;
  max?: number;
  min?: number;
  fixOverlimit?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
}>();

const step = computed(() => props.step ?? 1);
const max = computed(() => props.max ?? 999);
const min = computed(() => props.min ?? 0);

const increaseRef = ref<HTMLButtonElement>();

const getNumber = (value: string): number | null => {
  const number = Number.parseInt(value, 10);
  if (!value || Number.isNaN(number) || number < 0) {
    return null;
  }
  return number;
};

const model = computed<string>({
  get: () => String(props.modelValue),
  set: (value: string) => {
    const number = getNumber(value);
    if (number && number > 0) {
      emit("update:modelValue", number);
    }
  },
});

const onBlur = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.trim();

  const number = getNumber(value);

  if (value === "") {
    emit("update:modelValue", min.value);
  }
  else if (number === null) {
    // noop
  }
  else if (number <= min.value) {
    emit("update:modelValue", min.value);
  }
  else if (props.fixOverlimit && number > max.value) {
    emit("update:modelValue", max.value);
  }
  else {
    emit("update:modelValue", number);
  }

  input.value = String(props.modelValue);
};

const increase = () => {
  let nextValue = props.modelValue + step.value;
  if (nextValue > max.value) {
    nextValue = max.value;
  }
  emit("update:modelValue", nextValue);
};
const decrease = () => {
  let nextValue = props.modelValue - step.value;
  if (nextValue < min.value) {
    nextValue = min.value;
  }
  emit("update:modelValue", nextValue)
};
const focus = () => increaseRef.value?.focus();

defineExpose({
  focus,
});
</script>

<script lang="ts">
import { defineComponent } from "vue";
let id = 1;

export default defineComponent({
  data() {
    return { id: id++ };
  },
});
</script>